import { useState } from 'react';
import { api } from '../lib/api.js';
import { fmtTime } from '../lib/format.js';
import { openSignedDoc } from '../lib/uploads.js';
import { SOPModal } from './SOPModal.jsx';
import { FlagModal } from './FlagModal.jsx';
import { ReferencePhotoStrip } from './ReferencePhotoStrip.jsx';
import { useTaskPhotos } from '../lib/task-photos.js';

// Task status → left-border/background + status dot color (used only here).
const STATUS_COLORS = {
  completed: 'border-l-green-500 bg-green-50',
  pending: 'border-l-gray-300 bg-white',
  overdue: 'border-l-red-500 bg-red-50',
};
const STATUS_DOT = { completed: 'bg-green-500', pending: 'bg-gray-300', overdue: 'bg-red-500' };

// ─── Task Card ────────────────────────────────────────────────────────────────
export function TaskCard({ task, onComplete, userId, locationId, date, isManager, onFlagSubmit, onEdit }) {
  const isMyTask = task.assigned_user_id && task.assigned_user_id === userId;
  const [expanded, setExpanded] = useState(false);
  const [sopOpen, setSopOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [linkedOpen, setLinkedOpen] = useState(null); // {type:'recipe'|'doc', id, content}
  const [completing, setCompleting] = useState(false);
  const [measurementInput, setMeasurementInput] = useState('');
  const done = task.status === 'completed';
  const photos = useTaskPhotos(task.location_id, task.id);
  const needsMeasurement = task.measurement_required && !done;

  async function handleComplete(e) {
    e.stopPropagation();
    if (needsMeasurement && !measurementInput) return;
    setCompleting(true);
    try { await onComplete(task.id, needsMeasurement ? parseFloat(measurementInput) : undefined); }
    finally { setCompleting(false); }
  }

  return (
    <>
      {sopOpen && task.sop_id && <SOPModal sopId={task.sop_id} onClose={() => setSopOpen(false)} />}
      {flagOpen && <FlagModal task={task} locationId={locationId} userId={userId} onClose={() => setFlagOpen(false)} onSubmit={() => { setFlagOpen(false); onFlagSubmit(); }} />}
      <div className={`task-card rounded-xl border-l-4 p-4 cursor-pointer select-none ${isMyTask ? 'border-l-gold bg-amber-50/60' : (STATUS_COLORS[task.status] || STATUS_COLORS.pending)}`}
        onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || STATUS_DOT.pending}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`font-semibold text-sm ${done ? 'text-gray-400 line-through' : 'text-ink'}`}>{task.title}</p>
              {task.sop_id && <span className="text-xs bg-gold/20 text-yellow-700 rounded px-1.5 py-0.5 font-medium border border-gold/30">SOP</span>}
              {photos.length > 0 && <span className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-medium border border-slate-200">📷 {photos.length}</span>}
              {task.flagged && <span className="text-xs bg-red-100 text-red-700 rounded px-1.5 py-0.5 font-medium border border-red-200">🚩 Disputed</span>}
              {task.equipment_id && <span className="text-xs bg-blue-50 text-blue-600 rounded px-1.5 py-0.5 font-medium border border-blue-100">⚙ {task.equipment_name}</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {isMyTask && <span className="text-xs bg-gold/20 text-yellow-700 px-1.5 py-0.5 rounded font-semibold border border-gold/30">Your task</span>}
              <span className="text-xs text-gray-500">
                {task.assigned_user_id ? `→ ${task.assigned_user_name || 'Staff'}` : task.assigned_role}
              </span>
              {task.due_time && <span className="text-xs text-gray-400">· {task.due_time}</span>}
              {done && task.completed_by_name && (
                <span className="text-xs text-green-600 font-medium">· ✓ {task.completed_by_name}{task.completed_at ? ` at ${fmtTime(task.completed_at)}` : ''}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {needsMeasurement && (
              <div className="flex items-center gap-1">
                <input type="number" step="0.1" value={measurementInput} onChange={e => setMeasurementInput(e.target.value)}
                  className="w-20 border border-blue-300 rounded px-2 py-1.5 text-xs text-ink bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  placeholder={task.measurement_unit || '°F'} />
                <span className="text-xs text-gray-500 flex-shrink-0">{task.measurement_unit || '°F'}</span>
              </div>
            )}
            {!done && (
              <button onClick={handleComplete} disabled={completing || (needsMeasurement && !measurementInput)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95 ${needsMeasurement && !measurementInput ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-crimson text-white hover:bg-crimson-dark'}`}>
                {completing ? '...' : 'Complete'}
              </button>
            )}
            {/* Completed tasks show inline Undo + Report Issue (dispute)
                buttons so a closer can flag an opener's work, or an
                accidental tap can be reverted, without expanding the
                card. The "✓ Done" badge stays visible to confirm the
                completed state. */}
            {done && (
              <>
                <span className="text-xs font-semibold px-3 py-2 bg-green-100 text-green-700 rounded-lg">✓ Done</span>
                {(task.completed_by === userId || isManager) && task.completion_id && !task.flagged && (
                  <button onClick={async (e) => {
                    e.stopPropagation();
                    if (!confirm('Undo this completion? It will return to the open list.')) return;
                    try {
                      await api('DELETE', `/api/task-completions/${task.completion_id}`, null, { 'x-user-id': String(userId) });
                      onFlagSubmit();
                    } catch (err) {
                      let m = err.message; try { m = JSON.parse(err.message).error; } catch {}
                      alert(`Undo failed: ${m}`);
                    }
                  }} className="px-3 py-2 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 active:scale-95 transition-all">
                    Undo
                  </button>
                )}
                {!task.flagged && (
                  <button onClick={(e) => { e.stopPropagation(); setFlagOpen(true); }}
                    className="px-3 py-2 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 active:scale-95 transition-all">
                    🚩 Report
                  </button>
                )}
                {task.flagged && (
                  <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 border border-red-200">🚩 Reported</span>
                )}
              </>
            )}
          </div>
          <span className={`text-gray-400 text-lg transition-transform duration-200 flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} onClick={() => setExpanded(e => !e)}>⌄</span>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 fade-in">
            {task.description && <p className="text-sm text-gray-600 leading-relaxed mb-4">{task.description}</p>}
            {photos.length > 0 && <div className="mb-4"><ReferencePhotoStrip photos={photos} /></div>}
            <div className="flex gap-2 flex-wrap">
              {task.sop_id && (
                <button onClick={e => { e.stopPropagation(); setSopOpen(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-ink text-cream text-sm rounded-lg hover:bg-ink/80 active:scale-95 transition-all">
                  📋 View SOP
                </button>
              )}
              {task.sop_link_recipe_id && task.sop_link_recipe_name && (
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (linkedOpen?.type === 'recipe') { setLinkedOpen(null); return; }
                  try {
                    const r = await api('GET', `/api/recipes/${task.sop_link_recipe_id}`);
                    setLinkedOpen({ type: 'recipe', id: task.sop_link_recipe_id, content: r.recipe });
                  } catch (err) { setLinkedOpen({ type: 'error', message: err.message }); }
                }}
                  className="flex items-center gap-2 px-4 py-2 bg-forest text-cream text-sm rounded-lg hover:opacity-90 active:scale-95 transition-all">
                  📖 Recipe: {task.sop_link_recipe_name}
                </button>
              )}
              {task.sop_link_doc_id && task.sop_link_doc_title && (
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (linkedOpen?.type === 'doc') { setLinkedOpen(null); return; }
                  try {
                    const r = await api('GET', `/api/knowledge-docs/${task.sop_link_doc_id}`);
                    setLinkedOpen({ type: 'doc', id: task.sop_link_doc_id, content: r.document });
                  } catch (err) { setLinkedOpen({ type: 'error', message: err.message }); }
                }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-cream text-sm rounded-lg hover:opacity-90 active:scale-95 transition-all">
                  📄 {task.sop_link_doc_type === 'policy' ? 'Policy' : 'Doc'}: {task.sop_link_doc_title}
                </button>
              )}
              {Array.isArray(task.linked_documents) && task.linked_documents.map((d) => (
                <a key={d.id} href="#"
                  onClick={e => { e.preventDefault(); e.stopPropagation();
                    if (d.attachment_filename) openSignedDoc(d.id, d.attachment_filename, { 'x-user-id': String(userId) }); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-800 text-sm rounded-lg hover:bg-blue-100 active:scale-95 transition-all border border-blue-200">
                  📎 {d.title}
                </a>
              ))}
              {done && (
                <button onClick={e => { e.stopPropagation(); setFlagOpen(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 active:scale-95 transition-all border border-red-200">
                  🚩 Report Issue
                </button>
              )}
            </div>
            {linkedOpen?.type === 'recipe' && linkedOpen.content && (
              <div className="mt-3 p-3 rounded-lg border border-forest/30 bg-emerald-50/40 fade-in" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm font-bold text-ink mb-2">{linkedOpen.content.name}</div>
                {linkedOpen.content.notes && <p className="text-xs text-gray-600 mb-2">{linkedOpen.content.notes}</p>}
                {linkedOpen.content.ingredients?.length > 0 && (
                  <>
                    <div className="text-xs font-semibold text-gray-500 uppercase mt-2 mb-1">Ingredients</div>
                    <ul className="text-xs space-y-0.5 mb-2">
                      {linkedOpen.content.ingredients.map((ing) => (
                        <li key={ing.id}>· {ing.quantity} {ing.unit} {ing.name}</li>
                      ))}
                    </ul>
                  </>
                )}
                {linkedOpen.content.steps?.length > 0 && (
                  <>
                    <div className="text-xs font-semibold text-gray-500 uppercase mt-2 mb-1">Steps</div>
                    <ol className="text-xs space-y-1 list-decimal list-inside">
                      {linkedOpen.content.steps.map((s) => <li key={s.id}>{s.instruction}</li>)}
                    </ol>
                  </>
                )}
              </div>
            )}
            {linkedOpen?.type === 'doc' && linkedOpen.content && (
              <div className="mt-3 p-3 rounded-lg border border-blue-700/30 bg-blue-50/60 fade-in" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm font-bold text-ink mb-2">{linkedOpen.content.title}</div>
                {linkedOpen.content.short_id && <div className="text-xs text-gray-500 mb-1">{linkedOpen.content.short_id}</div>}
                <div className="text-xs whitespace-pre-wrap">{linkedOpen.content.content}</div>
              </div>
            )}
            {linkedOpen?.type === 'error' && (
              <div className="mt-3 p-2 rounded text-xs text-red-700 bg-red-50">Failed to load linked content: {linkedOpen.message}</div>
            )}
            {task.recurrence_days && task.recurrence === 'weekly' && (
              <p className="text-xs text-gray-400 mt-3">
                Runs: {JSON.parse(task.recurrence_days || '[]').join(', ')}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
